import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Search,
  Filter,
  RefreshCw,
  Plus,
  X,
  Building2,
  ListChecks,
  Target,
  Folder,
  FileText,
} from "lucide-react";
import { amsCheckpointService, AmsCheckpoint } from "../../../../services/amsCheckpointService";
import { Asset } from "../../../../types/asset";
import AmsCheckpointDrawer from "./AmsCheckpointDrawer";
import Button from "../../../../shared/components/ui/button/Button";

// Brand Colors
const BRAND_COLORS = {
  primary: "#339274",
  primaryHover: "#29795f",
  primaryLight: "#F8FCFA",
  primarySoft: "#EDF7F2",
};

// AMS Asset Card Component
interface AmsAssetCardProps {
  asset: Asset;
  checkpointCount: number;
  onView: (asset: Asset) => void;
}

const AmsAssetCard: React.FC<AmsAssetCardProps> = ({ asset, checkpointCount, onView }) => {
  const projectTypeName =
    typeof asset.projectTypeId === "object" && asset.projectTypeId
      ? asset.projectTypeId.name
      : "Unknown";

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2, scale: 1.02 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="relative bg-white rounded-2xl shadow-sm hover:shadow-md border border-gray-100 p-5 transition-all duration-200 group flex flex-col gap-4"
    >
      {/* Project Type Tag */}
      <div className="absolute right-0 top-0 m-0.5">
        <span className="text-[7px] font-normal text-[#339274] tracking-wide px-1 py-0.5 rounded-sm">
          {projectTypeName}
        </span>
      </div>

      {/* Asset Header */}
      <div className="flex items-start gap-3 pt-1">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#339274]/15 to-[#339274]/10 border border-[#339274]/20 flex-shrink-0">
          <Building2 size={20} className="text-[#339274]" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-800 group-hover:text-[#339274] transition-colors duration-200 leading-tight break-words">
            {asset.name}
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">Asset</p>
        </div>
      </div>

      {/* Checkpoints Section */}
      <div
        onClick={() => onView(asset)}
        className="flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-gray-50 to-gray-100/50 border border-gray-200/50 hover:from-gray-100 hover:to-gray-200/70 hover:border-[#339274]/30 transition-all duration-200 cursor-pointer"
      >
        <div className="flex items-center gap-2.5 flex-1">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#339274]/10 border border-[#339274]/20">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-[#339274]">
              <path
                d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div>
            <span className="text-xs font-medium text-gray-600 group-hover:text-[#339274] transition-colors">
              {checkpointCount} Checkpoints
            </span>
            <p className="text-[10px] text-gray-400 mt-0.5">View & manage</p>
          </div>
        </div>
        <div className="text-[#339274] opacity-60 group-hover:opacity-100 transition-opacity">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>
    </motion.div>
  );
};

// AMS Checkpoint Grid Component
interface AmsCheckpointGridProps {
  search: string;
  setSearch: (val: string) => void;
  checkpointTypeFilter: 'asset' | 'project' | 'all'; // Kept for compatibility but not used in UI
  setCheckpointTypeFilter: (val: 'asset' | 'project' | 'all') => void; // Kept for compatibility but not used in UI
  onBulkAdd: () => void;
  onAdd: () => void; // Single checkpoint create handler
  onAssetView: (asset: Asset) => void;
  onProjectView: (projectType: { _id: string; id?: string; name: string; code?: string }) => void;
  projectTypeId?: string;
  projectTypeName?: string;
}

const AmsCheckpointGrid: React.FC<AmsCheckpointGridProps> = ({
  search,
  setSearch,
  checkpointTypeFilter,
  setCheckpointTypeFilter,
  onBulkAdd,
  onAdd,
  onAssetView,
  onProjectView,
  projectTypeId,
  projectTypeName,
}) => {
  const navigate = useNavigate();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [assets, setAssets] = useState<Array<{ asset: Asset; count: number }>>([]);
  // Which sub-view is shown for the selected project type. Defaults to project-level.
  const [viewMode, setViewMode] = useState<'project' | 'assets'>('project');

  // Clear assets on mount if no project type is selected
  useEffect(() => {
    if (!projectTypeId) {
      setAssets([]);
    }
  }, []); // Only run on mount

  // Fetch AMS checkpoints grouped by asset and project
  const fetchAmsCheckpoints = async () => {
    // Don't fetch if no project type is selected
    if (!projectTypeId) {
      setAssets([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const filters: any = {};

      // Project type is required - only fetch when it's selected
      filters['project-type-id'] = projectTypeId;

      // Only fetch asset-type checkpoints since projects are shown separately
      filters['checkpoint-type'] = 'asset';

      if (search) {
        filters.search = search;
      }

      // Fetch without pagination to get all results
      const response = await amsCheckpointService.getAmsCheckpoints(filters);

      const checkpoints: AmsCheckpoint[] = response.data || [];

      // Group by asset
      const assetMap = new Map<string, { asset: Asset; count: number }>();

      checkpoints.forEach((checkpoint) => {
        if (checkpoint.checkpointType === 'asset' && checkpoint.assetId) {
          const assetId = typeof checkpoint.assetId === 'string' ? checkpoint.assetId : checkpoint.assetId._id || checkpoint.assetId.id;
          if (assetId) {
            const existing = assetMap.get(assetId);
            if (existing) {
              existing.count++;
            } else {
              const assetObj = typeof checkpoint.assetId === 'object' ? checkpoint.assetId : { _id: assetId, name: 'Unknown Asset' };
              assetMap.set(assetId, {
                asset: {
                  _id: assetId,
                  id: assetId,
                  name: typeof assetObj === 'object' && 'name' in assetObj ? assetObj.name : 'Unknown Asset',
                  projectTypeId: checkpoint.projectTypeId,
                } as Asset,
                count: 1,
              });
            }
          }
        }
      });

      setAssets(Array.from(assetMap.values()));
    } catch (error) {
      console.error('Failed to fetch AMS checkpoints:', error);
      toast.error('Failed to load AMS checkpoints');
      setAssets([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Clear assets immediately when projectTypeId is empty
    if (!projectTypeId) {
      setAssets([]);
      setIsLoading(false);
      return;
    }
    fetchAmsCheckpoints();
  }, [search, projectTypeId]);

  // Clear assets when component unmounts or when switching away (cleanup)
  useEffect(() => {
    return () => {
      // Cleanup: clear assets when component unmounts
      setAssets([]);
    };
  }, []);

  useEffect(() => {
    const handleShortcut = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);

  useEffect(() => {
    const handleKeyboard = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && searchInputRef.current === document.activeElement) {
        setSearch('');
      }
    };
    document.addEventListener('keydown', handleKeyboard);
    return () => document.removeEventListener('keydown', handleKeyboard);
  }, [setSearch]);

  // Filter assets
  const filteredAssets = assets.filter((item) => {
    if (!search.trim()) return true;
    return item.asset.name.toLowerCase().includes(search.toLowerCase());
  });

  const displayAssets = filteredAssets;

  return (
    <div className="space-y-6">
      {/* Sticky Filter Bar */}
      <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-white to-gray-25">
        {/* Main Controls Row */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Left Side: Search */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            {/* Search Bar */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={16} className="text-gray-400" />
              </div>
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name... (Ctrl+K)"
                className="block w-full sm:w-80 pl-9 pr-4 py-2.5 text-sm font-medium border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all duration-200 bg-white"
                style={{
                  '--tw-ring-color': BRAND_COLORS.primary,
                  'focusBorderColor': BRAND_COLORS.primary
                } as React.CSSProperties}
              />
            </div>
          </div>

          {/* Right Side: Add Buttons */}
          <div className="flex items-center gap-3">
            {/* Bulk Create Button */}
            <button
              onClick={() => {
                const searchParams = new URLSearchParams();
                if (projectTypeId) {
                  searchParams.set('projectTypeId', projectTypeId);
                }
                const queryString = searchParams.toString();
                navigate(`/masters/checkpoints/bulk-ams${queryString ? `?${queryString}` : ''}`);
              }}
              className="inline-flex items-center gap-3 px-5 py-2.5 text-sm font-medium text-white border border-transparent rounded-xl shadow-lg shadow-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-blue-200"
              style={{
                background: `linear-gradient(135deg, #3B82F6, #1D4ED8)`,
                '--tw-ring-color': '#3B82F6'
              } as React.CSSProperties}
            >
              <div className="relative">
                <ListChecks size={16} className="absolute animate-ping opacity-40" />
                <ListChecks size={16} className="relative" />
              </div>
              <span>Create Checkpoints</span>
            </button>

            {/* Upload Excel Button */}
            <button
              onClick={() => {
                const searchParams = new URLSearchParams();
                if (projectTypeId) {
                  searchParams.set('projectTypeId', projectTypeId);
                }
                const queryString = searchParams.toString();
                navigate(`/masters/checkpoints/bulk-ams-upload${queryString ? `?${queryString}` : ''}`);
              }}
              className="inline-flex items-center gap-3 px-5 py-2.5 text-sm font-medium text-white border border-transparent rounded-xl shadow-lg shadow-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-green-200"
              style={{
                background: `linear-gradient(135deg, #10B981, #059669)`,
                '--tw-ring-color': '#10B981'
              } as React.CSSProperties}
            >
              <div className="relative">
                <FileText size={16} className="absolute animate-ping opacity-40" />
                <FileText size={16} className="relative" />
              </div>
              <span>Upload Excel</span>
            </button>
          </div>
        </div>
      </div>

      {/* Cards Grid */}
      <div className="px-4">
        {!projectTypeId ? (
          <div className="flex flex-col items-center justify-center py-10 text-center bg-gradient-to-b from-white via-blue-50 to-white rounded-xl shadow-inner">
            <Target size={36} className="text-blue-100 mb-2" />
            <h3 className="text-base font-semibold text-gray-800 mb-1">Please select a Project Type to view checkpoints</h3>
            <p className="text-xs text-gray-500">Select a project type from the tabs above to see its checkpoints.</p>
          </div>
        ) : (
          <>
            {/* View toggle: Project-level Checkpoints vs Assets */}
            <div className="mb-4 inline-flex rounded-lg border border-gray-200 bg-white p-0.5 shadow-sm">
              <button
                type="button"
                onClick={() => setViewMode('project')}
                aria-pressed={viewMode === 'project'}
                className={`inline-flex items-center gap-1.5 rounded-md px-3.5 py-2 text-xs font-semibold transition-colors ${
                  viewMode === 'project' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Folder size={14} /> Project-level Checkpoints
              </button>
              <button
                type="button"
                onClick={() => setViewMode('assets')}
                aria-pressed={viewMode === 'assets'}
                className={`inline-flex items-center gap-1.5 rounded-md px-3.5 py-2 text-xs font-semibold transition-colors ${
                  viewMode === 'assets' ? 'bg-[#339274] text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Building2 size={14} /> Assets
              </button>
            </div>

            {viewMode === 'project' ? (
              /* Project-level Checkpoints — manage inline (same data + actions the drawer
                 shows), since there's only ever one project type in context here. */
              <AmsCheckpointDrawer
                open
                embedded
                type="project"
                projectType={{ _id: projectTypeId, id: projectTypeId, name: projectTypeName || 'Project Type' }}
                onClose={() => {}}
              />
            ) : isLoading ? (
              <div className="flex items-center justify-center py-10">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-7 h-7 border-2 border-blue-100 border-t-[#339274] rounded-full animate-spin" />
                  <span className="text-sm font-medium text-gray-700">Loading checkpoints...</span>
                </div>
              </div>
            ) : displayAssets.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center bg-gradient-to-b from-white via-blue-50 to-white rounded-xl shadow-inner">
                <Target size={36} className="text-blue-100 mb-2" />
                <h3 className="text-base font-semibold text-gray-800 mb-1">
                  {search.trim() ? 'No assets found matching your search' : 'No asset checkpoints found'}
                </h3>
                <p className="text-xs text-gray-500">
                  {search.trim() ? 'Try adjusting your search terms' : 'No asset checkpoints available for this project type'}
                </p>
                {search.trim() && (
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => setSearch("")}
                      className="px-3 py-1.5 rounded bg-gradient-to-r from-blue-400 to-blue-600 text-white text-xs font-medium shadow hover:from-blue-500 hover:to-blue-700 transition"
                    >
                      Clear Search
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Search Results Info */}
                {search.trim() && (
                  <div className="mb-4 p-2 bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-100 rounded-lg shadow-sm">
                    <div className="flex items-center gap-2 text-blue-900">
                      <Search size={14} className="opacity-80" />
                      <span className="text-xs font-medium">
                        Showing <span className="text-blue-700">{displayAssets.length}</span> item{displayAssets.length !== 1 ? 's' : ''} matching <span className="font-mono bg-blue-200/60 px-1 py-0.5 rounded text-blue-900">"{search}"</span>
                      </span>
                    </div>
                  </div>
                )}

                {/* Assets Grid */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Assets</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {displayAssets.map((item) => (
                      <div key={item.asset.id || item.asset._id} className="transition-transform hover:scale-[1.01]">
                        <AmsAssetCard
                          asset={item.asset}
                          checkpointCount={item.count}
                          onView={onAssetView}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>

    </div>
  );
};

export default AmsCheckpointGrid;
